import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { FigureNodeView } from "./figure-node-view";

/**
 * Figure node — an `<img>` wrapped in a `<figure>` with an editable
 * `<figcaption>`. The caption is the node's inline content (real editable
 * text), while `src`/`alt`/`title`/`width` are attributes carried on the
 * nested img.
 *
 * Owned locally (rather than a community extension) so the persisted Tiptap
 * JSON schema stays under our control — chapter content lives in the DB
 * forever, and the reader renders it with a hand-written walker, not Tiptap.
 */
interface FigureOptions {
    HTMLAttributes: Record<string, unknown>;
}

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        figure: {
            /** Insert a figure with an image and optional caption. */
            setFigure: (options: {
                src: string;
                alt?: string | null;
                title?: string | null;
                caption?: string;
                naturalWidth?: number | null;
                naturalHeight?: number | null;
            }) => ReturnType;
        };
    }
}

export const Figure = Node.create<FigureOptions>({
    name: "figure",

    addOptions() {
        return { HTMLAttributes: {} };
    },

    group: "block",
    content: "inline*",
    draggable: true,
    isolating: true,

    addAttributes() {
        return {
            src: {
                default: null,
                parseHTML: (el) =>
                    el.querySelector("img")?.getAttribute("src") ?? null,
            },
            alt: {
                default: null,
                parseHTML: (el) =>
                    el.querySelector("img")?.getAttribute("alt") ?? null,
            },
            title: {
                default: null,
                parseHTML: (el) =>
                    el.querySelector("img")?.getAttribute("title") ?? null,
            },
            // Image width as a percentage of the column (null = full width).
            // Rendered manually in renderHTML, so suppress the default attr render.
            width: {
                default: null,
                parseHTML: (el) => {
                    const raw = el
                        .querySelector("img")
                        ?.style.width.replace("%", "");
                    const n = raw ? parseInt(raw, 10) : NaN;
                    return Number.isFinite(n) ? n : null;
                },
                renderHTML: () => ({}),
            },
            // Intrinsic pixel dimensions, captured at upload. Used to reserve
            // layout space (aspect-ratio) so images don't cause CLS on load.
            naturalWidth: {
                default: null,
                parseHTML: (el) => {
                    const raw = el.querySelector("img")?.getAttribute("width");
                    const n = raw ? parseInt(raw, 10) : NaN;
                    return Number.isFinite(n) ? n : null;
                },
                renderHTML: (attrs) =>
                    attrs.naturalWidth
                        ? { width: String(attrs.naturalWidth) }
                        : {},
            },
            naturalHeight: {
                default: null,
                parseHTML: (el) => {
                    const raw = el.querySelector("img")?.getAttribute("height");
                    const n = raw ? parseInt(raw, 10) : NaN;
                    return Number.isFinite(n) ? n : null;
                },
                renderHTML: (attrs) =>
                    attrs.naturalHeight
                        ? { height: String(attrs.naturalHeight) }
                        : {},
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: "figure",
                // Only treat a <figure> as ours when it actually wraps an image.
                getAttrs: (el) =>
                    (el as HTMLElement).querySelector("img") ? null : false,
                // The caption text is parsed from the figcaption child.
                contentElement: "figcaption",
            },
        ];
    },

    renderHTML({ node, HTMLAttributes }) {
        const width = node.attrs.width as number | null;
        return [
            "figure",
            mergeAttributes(this.options.HTMLAttributes),
            [
                "img",
                mergeAttributes(HTMLAttributes, {
                    draggable: "false",
                    contenteditable: "false",
                    ...(width ? { style: `width: ${width}%` } : {}),
                }),
            ],
            ["figcaption", 0],
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(FigureNodeView);
    },

    addCommands() {
        return {
            setFigure:
                ({ caption, ...attrs }) =>
                ({ commands }) =>
                    commands.insertContent({
                        type: this.name,
                        attrs,
                        content: caption
                            ? [{ type: "text", text: caption }]
                            : [],
                    }),
        };
    },
});
